import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Award,
  Bolt,
  CalendarDays,
  CheckCircle2,
  Crown,
  Eye,
  FileText,
  Languages,
  Medal,
  Rocket,
  Send,
  Sparkles,
  Star,
  TrendingUp,
  UserRound,
} from "lucide-react";
import ApplicantShell from "../components/ApplicantShell";
import ApplicantCoachPanel from "../components/applicant/ApplicantCoachPanel";
import { useApplicantCoach } from "../components/applicant/ApplicantCoachContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import config from "../config/config";
import { buildAuthFetchOptions, clearStoredAuth } from "../utils/authSession";

const medalIcons = {
  user: UserRound,
  file: FileText,
  spark: Sparkles,
  language: Languages,
  check: CheckCircle2,
  send: Send,
  eye: Eye,
  medal: Medal,
  crown: Crown,
  trend: TrendingUp,
  calendar: CalendarDays,
  bolt: Bolt,
  repeat: Award,
  star: Star,
  rocket: Rocket,
};

async function fetchJson(path) {
  const response = await fetch(`${config.API_BASE_URL}${path}`, buildAuthFetchOptions());
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.message || data?.detail || `Request failed (${response.status})`);
    error.status = response.status;
    error.detail = data?.detail || "";
    throw error;
  }
  return data;
}

export default function ApplicantAchievements() {
  const navigate = useNavigate();
  const { registerCoachSurface } = useApplicantCoach();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.");

  useEffect(() => {
    document.title = "Experts4Europe | Applicant Erfolge";
  }, []);

  const handleAuthError = (err) => {
    if (Number(err?.status) !== 401) return false;
    clearStoredAuth();
    const detail = String(err?.detail || err?.message || "").toLowerCase();
    const isTimeout = detail.includes("expired") || detail.includes("inactivity") || detail.includes("session");
    setSessionExpiredMessage(
      isTimeout
        ? "Du wurdest wegen Inaktivitaet automatisch abgemeldet. Bitte melde dich erneut an."
        : "Deine Anmeldung ist nicht mehr gueltig. Bitte melde dich erneut an."
    );
    setSessionExpiredOpen(true);
    return true;
  };

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchJson("/api/applicant-dashboard/achievements");
        if (active) setSnapshot(data);
      } catch (err) {
        if (handleAuthError(err)) return;
        if (active) setError(err.message || "Die Erfolgsseite konnte nicht geladen werden.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const categories = snapshot?.achievements?.categories || [];
  const spotlight = snapshot?.achievements?.spotlight || [];
  const nextMedal = snapshot?.achievements?.summary?.next_medal || null;
  const unlockedCount = snapshot?.achievements?.summary?.unlocked_count || 0;
  const totalCount = snapshot?.achievements?.summary?.total_count || 0;
  const actions = snapshot?.actions || {};
  const motivation = snapshot?.motivation || {};
  const coachSurfaceContext = useMemo(() => {
    const coachNextStep = actions?.primary
      ? {
          key: `achievements_${actions.primary.key || "next"}`,
          label_de: actions.primary.label || "Naechster Schritt",
          label_en: "next step",
          reason_de: actions.primary.reason || motivation.next_step_gain || "Das ist dein naechster sichtbarer Fortschrittsschritt.",
          href: actions.primary.href || "/dashboard/applicant",
        }
      : {
          key: "achievements_dashboard",
          label_de: "Dashboard oeffnen",
          label_en: "open your dashboard",
          reason_de: "Gerade ist kein weiterer sichtbarer Schritt hervorgehoben.",
          href: "/dashboard/applicant",
        };
    const coachMissingItems = nextMedal
      ? [{
          key: `achievement_${nextMedal.key || "next_medal"}`,
          label_de: nextMedal.label || "Naechster Meilenstein",
          label_en: "next milestone",
          reason_de: nextMedal.howTo || motivation.next_milestone_label || "Das ist dein naechster sichtbarer Erfolg.",
          href: actions?.primary?.href || "/dashboard/applicant",
        }]
      : [];

    return {
      screen_key: "applicant_achievements",
      screen_label: "Erfolge",
      highlighted_step: `${coachNextStep.label_de}: ${coachNextStep.reason_de}`,
      surface_state_key: `achievements:${loading ? "loading" : `${unlockedCount}-${totalCount}`}:${nextMedal?.key || "none"}:${spotlight.length}:${error ? "error" : "ok"}`,
      surface_summary: {
        surface_type: "achievements",
        status_label: loading
          ? "Erfolge werden geladen"
          : `${unlockedCount} von ${totalCount} Erfolgen freigeschaltet`,
        status_note: loading
          ? "Die sichtbaren Erfolge werden gerade geladen."
          : nextMedal
            ? `${nextMedal.label || "Naechster Meilenstein"} ist als Naechstes sichtbar erreichbar.`
            : "Alle sichtbaren Erfolge sind aktuell freigeschaltet.",
        unlocked_count: unlockedCount,
        total_count: totalCount,
        next_milestone: nextMedal ? {
          key: nextMedal.key || "next_medal",
          label: nextMedal.label || "Naechster Meilenstein",
          detail: nextMedal.howTo || motivation.next_milestone_label || "",
          progress_percent: nextMedal.progress_percent || 0,
        } : null,
        spotlight_labels: spotlight.slice(0, 4).map((item) => item.label || ""),
        compare_label: motivation.compare_label || "",
        place_label: motivation.place_label || "",
        next_step_gain: motivation.next_step_gain || "",
        coach_next_step: coachNextStep,
        coach_missing_items: coachMissingItems,
      },
    };
  }, [actions, error, loading, motivation, nextMedal, spotlight, totalCount, unlockedCount]);

  useEffect(() => {
    registerCoachSurface(coachSurfaceContext);
  }, [coachSurfaceContext, registerCoachSurface]);

  const searchItems = useMemo(() => ([
    { href: "/dashboard/applicant", label: "Dashboard", description: "Zurueck zur Startseite" },
    { href: actions?.primary?.href || "/dashboard/applicant", label: actions?.primary?.label || "Naechster Schritt", description: actions?.primary?.reason || "Dein naechster sinnvoller Schritt" },
  ]), [actions]);

  return (
    <ApplicantShell
      title="Deine Erfolge"
      subtitle="Sieh, was du schon geschafft hast und was als Naechstes moeglich ist."
      actions={(
        <>
          <Button variant="outline" className="applicant-cta-button applicant-cta-button-secondary" onClick={() => navigate("/dashboard/applicant")}>
            Zurueck zum Dashboard
          </Button>
          <Button className="applicant-cta-button" onClick={() => navigate(actions?.primary?.href || "/dashboard/applicant")}>
            {actions?.primary?.label || "Naechster Schritt"}
          </Button>
        </>
      )}
      searchItems={searchItems}
      notifications={[]}
    >
      <div className="applicant-achievements-page">
        {error ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{error}</div> : null}

        <section className="applicant-achievements-hero">
          <div className="applicant-achievements-hero__main">
            <span className="applicant-section-kicker">Erfolge</span>
            <h2>Du kommst voran.</h2>
            <p>{motivation.week_label || "Hier siehst du deine freigeschalteten Erfolge und deinen naechsten Meilenstein."}</p>
            <div className="applicant-achievements-hero__stats">
              <article className="applicant-achievements-stat">
                <span>Freigeschaltet</span>
                <strong>{loading ? "--" : unlockedCount}</strong>
                <small>von {totalCount || "--"} Erfolgen</small>
              </article>
              <article className="applicant-achievements-stat">
                <span>Dein Vergleich</span>
                <strong>{loading ? "--" : motivation.compare_label || "Noch offen"}</strong>
                <small>{motivation.compare_detail || "deine Gruppe"}</small>
              </article>
              <article className="applicant-achievements-stat">
                <span>Diese Woche</span>
                <strong>{loading ? "--" : motivation.place_label || "Noch offen"}</strong>
                <small>{motivation.change_label || "dein Wochenstand"}</small>
              </article>
            </div>
          </div>

          <aside className="applicant-achievements-next">
            <span className="applicant-section-kicker">Naechster Erfolg</span>
            <strong>{nextMedal?.label || "Du hast alle sichtbaren Erfolge frei"}</strong>
            <p>{nextMedal?.howTo || motivation.next_milestone_label || "Halte deinen Stand."}</p>
            <div className="applicant-achievements-next__progress">
              <div className="applicant-achievements-next__track">
                <span style={{ width: `${nextMedal?.progress_percent || 0}%` }} />
              </div>
              <small>{nextMedal ? `${nextMedal.progress_percent}% geschafft` : "Alles sichtbar"}</small>
            </div>
          </aside>
        </section>

        <section className="applicant-achievements-spotlight">
          <div className="applicant-module-card__head">
            <div>
              <span className="applicant-section-kicker">Schon geschafft</span>
              <h3>Deine starken Erfolge</h3>
            </div>
          </div>
          <div className="applicant-achievements-spotlight__grid">
            {loading ? <div className="applicant-empty-card">Erfolge werden geladen ...</div> : spotlight.length ? spotlight.map((item) => {
              const Icon = medalIcons[item.icon] || Medal;
              return (
                <article key={item.key} className={`applicant-medal-card is-unlocked tone-${item.tone || "sky"}`}>
                  <div className="applicant-medal-card__crest"><Icon size={22} strokeWidth={2} /></div>
                  <div className="applicant-medal-card__copy">
                    <span>{item.category}</span>
                    <strong>{item.label}</strong>
                    <small>{item.howTo}</small>
                  </div>
                </article>
              );
            }) : <div className="applicant-empty-card">Dein erster Erfolg kommt mit deinem naechsten Schritt.</div>}
          </div>
        </section>

        <div className="applicant-achievements-category-grid">
          {categories.map((group) => (
            <section key={group.key} className="applicant-achievement-group">
              <div className="applicant-module-card__head">
                <div>
                  <span className="applicant-section-kicker">{group.label}</span>
                  <h3>{group.label}</h3>
                </div>
              </div>
              <div className="applicant-achievement-group__grid">
                {group.medals.map((item) => {
                  const Icon = medalIcons[item.icon] || Medal;
                  return (
                    <article key={item.key} className={`applicant-medal-card ${item.unlocked ? "is-unlocked" : "is-locked"} tone-${item.tone || "sky"}`}>
                      <div className="applicant-medal-card__crest"><Icon size={22} strokeWidth={2} /></div>
                      <div className="applicant-medal-card__copy">
                        <span>{item.unlocked ? "Freigeschaltet" : "Noch gesperrt"}</span>
                        <strong>{item.label}</strong>
                        <small>{item.howTo}</small>
                      </div>
                      <div className="applicant-medal-card__progress">
                        <div className="applicant-medal-card__track">
                          <span style={{ width: `${item.progress_percent}%` }} />
                        </div>
                        <small>{item.progress_percent}%</small>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>

      <Dialog open={sessionExpiredOpen} onOpenChange={() => {}}>
        <DialogContent className="max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Sitzung abgelaufen</DialogTitle>
            <DialogDescription>{sessionExpiredMessage}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap justify-end gap-3">
            <Button
              variant="outline"
              className="applicant-cta-button applicant-cta-button-secondary"
              onClick={() => navigate("/")}
            >
              Startseite
            </Button>
            <Button
              className="applicant-cta-button"
              onClick={() => navigate("/login", { replace: true })}
            >
              Erneut anmelden
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <ApplicantCoachPanel />
    </ApplicantShell>
  );
}
